import cn from "clsx";
import { useTranslations } from "next-intl";
import { RiAddLine, RiSubtractLine } from "react-icons/ri";

import { LoayltyTier } from "@/features/account/components/loyalty-tier";
import { PriceItem } from "./quantity-table";

export const TableRow: React.FC<{
  price: PriceItem;
  isSelected?: boolean;
  className?: string;
  setQuantity?: (q: number) => void;
}> = ({ price, isSelected = false, className = "", setQuantity }) => {
  const t = useTranslations("custom-attributes-forms");
  return (
    <div
      className={cn(
        "text-grey-90 border-grey-60 flex h-12 cursor-pointer items-center justify-between border-t py-4 text-sm leading-none md:text-base",
        {
          "bg-berry-10 selected-quantity": isSelected,
        },
        {
          "hover:bg-grey-10": !isSelected,
        },
        className,
      )}
      {...(setQuantity
        ? {
            onClick: () => setQuantity(price.value),
          }
        : {})}
    >
      <span className="w-[80px] pl-2 md:w-[120px] md:pl-4">{price.value}</span>
      <span className="w-[52px] md:w-2/12">{price.unitPrice}</span>
      {price.discountTotalPrice ? (
        <span className="w-[92px] md:w-2/12">
          <span className="mr-2">{price.discountTotalPrice}</span>
          <span className="text-grey-80 text-xs line-through">{price.totalPrice}</span>
        </span>
      ) : (
        <span className="w-[92px] md:w-2/12">{price.totalPrice}</span>
      )}

      <span className="flex w-[82px] flex-col items-center justify-end pr-2 text-[10px] md:w-4/12 md:flex-row md:justify-center md:pr-4 md:text-sm">
        {isSelected ? (
          <SelectionBadge className="md:mx-auto" />
        ) : price.priceIncrease ? (
          <>
            {t("text-only")}{" "}
            <b className="px-1 text-sm font-normal md:text-base">{price.priceIncrease}</b>{" "}
            {t("text-extra")}
          </>
        ) : (
          ""
        )}
      </span>
    </div>
  );
};

export const SelectionBadge: React.FC<{ className?: string }> = ({ className = "" }) => {
  const t = useTranslations("custom-attributes-forms");
  return (
    <div className="flex items-center">
      <LoayltyTier iconOnly className="block mr-2" />

      <span
        className={cn(
          "bg-berry flex h-5 w-[82px] items-center justify-center whitespace-nowrap rounded-full px-2 text-[11px] text-white md:w-auto md:px-4 md:text-sm pl-2 ",
          className,
        )}
      >
        {t("text-selection")}
      </span>
    </div>
  );
};

export const TableHeader: React.FC<{
  quantityText: string;
  hideLastColumn?: boolean;
  className?: string;
}> = ({ quantityText, hideLastColumn = false, className = "" }) => {
  const t = useTranslations("custom-attributes-forms");

  return (
    <div
      className={cn(
        "text-grey-90 mb-4 flex h-6 justify-between text-xs font-normal leading-none md:h-3",
        className,
      )}
    >
      <span className="md:items-top flex h-full w-[80px] items-end text-left md:h-3 md:w-[120px]">
        {quantityText}
      </span>
      <span className="md:items-top flex h-full w-[52px] items-end md:w-2/12">
        {t("text-unit-price")}
      </span>
      <span className="md:items-top flex h-full w-[92px] items-end md:w-2/12 ">
        {t("text-total")}
      </span>
      <span className="md:items-top flex h-full w-[82px] items-end justify-end text-right italic md:w-4/12">
        {!hideLastColumn && t("text-vat-inc")}
      </span>
    </div>
  );
};

export const InputNumberControl: React.FC<{ onClick: (value: number) => void }> = ({ onClick }) => {
  return (
    <div className="flex h-full flex-col justify-between py-[5.5px] pr-[3px]">
      <span onClick={() => onClick(1)} className="bg-grey-80 size-4 cursor-pointer rounded-full">
        <RiAddLine className="text-white" />
      </span>
      <span onClick={() => onClick(-1)} className="bg-grey-80 size-4 cursor-pointer rounded-full">
        <RiSubtractLine className="text-white" />
      </span>
    </div>
  );
};

export const TableRowWithControlText: React.FC<{
  text: string;
  className?: string;
  onClick?: () => void;
}> = ({ text, className = "", onClick }) => {
  return (
    <div
      className={cn(
        "text-grey-90 border-grey-60 flex h-12 cursor-pointer items-center justify-center border-t px-3 text-center text-sm md:text-base",
        className,
      )}
      onClick={onClick}
    >
      {text}
    </div>
  );
};
